import {api, RequestParam} from "@/shared/api";

import {BasicDataTable, BasicDataTableConfig, TypedColumnSettings} from "@/shared/ui/basicDataTable";
import {ButtonRadioGroup} from "@/shared/ui/buttonRadioGroup";
import * as Constants from "@/shared/constants";
import {
	DashboardTable,
	DashboardTableBarPresentation,
	DashboardTableConfig,
	FooterDefinition
} from "@/features/reports/dashboardTable";
import {DashboardFilterPath} from "@/features/reports/dashboardFilterPath";
import {UserSettingsChanger} from "@/shared/userSettingsChanger";
import {TimeNavigationBar} from "@/shared/ui/timeNavigationBar";
import {statusElementsHolder} from "@/shared/recording/statusElementsHolder";
import {BrowserStorageSettings} from "@/shared/browserStorageSettings";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import {mergeProperties} from "@/shared/utils/misc";
import {formatDuration, getISODate} from "@/shared/utils/datefunctions";
import {
	RunningDriveLog,
	RunningDriveLogsResponse,
	RunningProjectTime,
	RunningProjectTimesResponse,
	RunningWorkingTime,
	RunningWorkingTimesResponse,
	DashboardUserEntry,
	UserDashboardResponse,
	WorkingTimeCategory,
	WorkingTimeTypeDashboardEntry,
	WorkingTimeTypeDashboardResponse
} from "@/shared/timr";
import {renderProgress} from "@/features/reports/progressBarHelper";

class UserDashboardState {
	public static EVENT_CHANGE_USER = "changeUser";

	private currentUserId ?: number = null;

	public changeUser(userId: number) {
		const oldUserId = this.currentUserId;
		this.currentUserId = userId;
		if (oldUserId !== userId) {
			$(this).triggerHandler(UserDashboardState.EVENT_CHANGE_USER, userId);
		}
	}

	public getUserId(): number | null {
		return this.currentUserId;
	}

	public onUserChanged(handler: (_eventObject: unknown, userId: number) => void) {
		$(this).on(UserDashboardState.EVENT_CHANGE_USER, handler);
	}
}

export class UserDashboardPage {

	public static COLUMN_SIZE_USER = "200px";
	public static COLUMN_SIZE_GPS_ICON = "56px";
	public static COLUMN_SIZE_DURATION = "120px";
	public static COLUMN_SIZE_DURATION_LONG = "160px";
	public static COLUMN_SIZE_DURATION_BAR = "300px";
	public static COLUMN_SIZE_PAUSED_AT = "140px";

	private dashboardState: UserDashboardState;

	private timeNavigationBar: TimeNavigationBar;
	private tablePresentationControl: ButtonRadioGroup<DashboardTableBarPresentation> = null;
	private filterPath: DashboardFilterPath;

	private readonly userDashboardUserTable: UserTable;
	private readonly userDashboardWorkingTimeTypeTable: WorkingTimeTypeTable;
	private readonly userDashboardRunningWorkingTimesTable: RunningWorkingTimesTable;
	private readonly userDashboardRunningProjectTimesTable: RunningProjectTimesTable;
	private readonly userDashboardRunningDriveLogsTable: RunningDriveLogsTable;

	constructor() {
		this.dashboardState = new UserDashboardState();
		this.dashboardState.onUserChanged((_eventObject, _userId) => {
			if (this.userDashboardUserTable) {
				this.userDashboardUserTable.filter(this.buildFilterParams());
			}
			if (this.userDashboardWorkingTimeTypeTable) {
				this.userDashboardWorkingTimeTypeTable.filter(this.buildFilterParams());
			}
			if (this.userDashboardRunningWorkingTimesTable) {
				this.userDashboardRunningWorkingTimesTable.filter(this.buildFilterParams());
			}
			if (this.userDashboardRunningProjectTimesTable) {
				this.userDashboardRunningProjectTimesTable.filter(this.buildFilterParams());
			}
			if (this.userDashboardRunningDriveLogsTable) {
				this.userDashboardRunningDriveLogsTable.filter(this.buildFilterParams());
			}
		});

		this.timeNavigationBar = new TimeNavigationBar({
			container: $(".breadcrumb-line-dashboard"),
			timeSpanChanged: (from, to, type) => {
				BrowserStorageSettings.setReportingDateFrom(Constants.GROUP_TIME_NAV_BAR, from);
				BrowserStorageSettings.setReportingDateTo(Constants.GROUP_TIME_NAV_BAR, to);
				BrowserStorageSettings.setReportingDateType(Constants.GROUP_TIME_NAV_BAR, type);
				if (this.userDashboardUserTable) {
					this.userDashboardUserTable.filter(this.buildFilterParams());
				}
				if (this.userDashboardWorkingTimeTypeTable) {
					this.userDashboardWorkingTimeTypeTable.filter(this.buildFilterParams());
				}
			},
			defaultDateType: BrowserStorageSettings.getReportingDateType(Constants.GROUP_TIME_NAV_BAR),
			defaultFromDate: BrowserStorageSettings.getReportingDateFrom(Constants.GROUP_TIME_NAV_BAR),
			defaultToDate: BrowserStorageSettings.getReportingDateTo(Constants.GROUP_TIME_NAV_BAR)
		});

		this.tablePresentationControl = ButtonRadioGroup.typed({
			value: DashboardTableBarPresentation.Absolute,
			button: document.getElementById("dashboardAbsoluteButton")
		}, {
			value: DashboardTableBarPresentation.Relative,
			button: document.getElementById("dashboardRelativeButton")
		}).onValueChanged((presentation) => {
			new UserSettingsChanger("userDashboardPresentation").changeSettingTo(DashboardTableBarPresentation[presentation]);
			if (this.userDashboardUserTable) {
				this.userDashboardUserTable.setPresentation(presentation);
			}
		});

		this.filterPath = new DashboardFilterPath({
			currentFilterPathElement: $("#filterPath"),
			onFilterPathElementClick: (_e: unknown, obj: number) => {
				this.dashboardState.changeUser(Number(obj));
			},
			currentFilterPathRootMessage: messages["report.rootUser"]
		});

		// the JSP only renders #userDashboardUserTable when working time or project time is enabled
		if (globals.workingTimeEnabled || globals.projectTimeEnabled) {
			this.userDashboardUserTable = new UserTable({
				element: $("#userDashboardUserTable"),
				initialFilter: this.buildFilterParams(),
				onRowClick: (data: DashboardUserEntry) => {
					if (this.isTableRowClickable(data)) {
						this.dashboardState.changeUser(Number(data.id));
					}
				},
				isRowClickable: (data: DashboardUserEntry) => this.isTableRowClickable(data),
				detailsPopover: true,
				presentation: this.tablePresentationControl.getValue(),
				orderable: true,
				clampNote: false,
				allowExpand: false,
			}).onDataChanged((response: UserDashboardResponse) => {
				this.dashboardState.changeUser(Number(response.id));
				this.filterPath.updateDashboardInfo(response);
			});
		}

		if (globals.workingTimeEnabled) {
			this.userDashboardWorkingTimeTypeTable = new WorkingTimeTypeTable({
				element: $("#userDashboardWorkingTimeTypeTable"),
				initialFilter: this.buildFilterParams(),
				orderable: true,
				clampNote: false,
				allowExpand: false,
			});
			this.userDashboardRunningWorkingTimesTable = new RunningWorkingTimesTable({
				element: $("#userDashboardRunningWorkingTimesTable"),
				initialFilter: this.buildFilterParams(),
				orderable: true,
				clampNote: true,
				allowExpand: true,
			});
		}
		if (globals.projectTimeEnabled) {
			this.userDashboardRunningProjectTimesTable = new RunningProjectTimesTable({
				element: $("#userDashboardRunningProjectTimesTable"),
				initialFilter: this.buildFilterParams(),
				orderable: true,
				clampNote: true,
				allowExpand: true,
			});
		}
		if (globals.driveLogEnabled) {
			this.userDashboardRunningDriveLogsTable = new RunningDriveLogsTable({
				element: $("#userDashboardRunningDriveLogsTable"),
				initialFilter: this.buildFilterParams(),
				orderable: true,
				clampNote: false,
				allowExpand: false,
			});
		}
	}

	private buildFilterParams(): RequestParam[] {
		return [{
			name: "id",
			value: this.dashboardState.getUserId()
		}, {
			name: "dateFrom",
			value: getISODate(this.timeNavigationBar.getDateFrom())
		}, {
			name: "dateTo",
			value: getISODate(this.timeNavigationBar.getDateTo())
		}];
	}

	private isTableRowClickable(data: DashboardUserEntry): boolean {
		return data && data.id && Number(data.id) !== this.dashboardState.getUserId();
	}
}

class UserTable extends DashboardTable<DashboardUserEntry, UserDashboardResponse> {
	private maxDuration: number;

	constructor(public config: DashboardTableConfig<DashboardUserEntry>) {
		super("/timr/reports/getUsersForUserDashboard.json", config);

		this.onDataChanged((json: UserDashboardResponse) => {
			const maxUserDurations = json.users.results.map((user) => Math.max(user.workingTimeDuration, user.duration));
			this.maxDuration = Math.max(...maxUserDurations);
		});
	}

	protected createDataTableConfig(): DataTables.Settings {
		return mergeProperties(super.createDataTableConfig(), {
			ajax: {
				dataSrc: "users.results"
			}
		});
	}

	protected getColumnSettings(): TypedColumnSettings<DashboardUserEntry>[] {
		return [
			{data: "description", className: "table-data-description", orderable: true, render: BasicDataTable.renderText},
			{
				data: "workingTimeDuration",
				width: UserDashboardPage.COLUMN_SIZE_DURATION_LONG,
				className: "text-right",
				orderable: true,
				visible: globals.workingTimeEnabled,
				render: BasicDataTable.renderDuration
			},
			{
				data: "duration",
				width: UserDashboardPage.COLUMN_SIZE_DURATION,
				className: "text-right",
				orderable: true,
				visible: globals.projectTimeEnabled,
				render: BasicDataTable.renderDuration
			},
			{
				data: "durationBillable",
				width: UserDashboardPage.COLUMN_SIZE_DURATION,
				className: "text-right",
				orderable: true,
				visible: globals.projectTimeEnabled,
				render: BasicDataTable.renderDuration
			},
			{
				data: "durationNotBillable",
				width: UserDashboardPage.COLUMN_SIZE_DURATION_LONG,
				className: "text-right",
				orderable: true,
				visible: globals.projectTimeEnabled,
				render: BasicDataTable.renderDuration
			},
			{
				data: "duration",
				width: UserDashboardPage.COLUMN_SIZE_DURATION_BAR,
				orderable: false,
				render: (data, type, recording, meta) => this.renderDurationBars(data, type, recording, meta)
			},
		];
	}

	protected createFooterDefinition(): FooterDefinition<UserDashboardResponse> {
		return {
			1: (json) => formatDuration(json.users.total.workingTimeDuration),
			2: (json) => formatDuration(json.users.total.duration),
			3: (json) => formatDuration(json.users.total.durationBillable),
			4: (json) => formatDuration(json.users.total.durationNotBillable)
		};
	}

	protected getDefaultOrder(): (string | number)[][] {
		return [[0, "asc"]];
	}

	private renderDurationBars(_data: number, _type: string, recording: DashboardUserEntry, _meta: DataTables.CellMetaSettings) {
		const durationAnchor = (this.presentation === DashboardTableBarPresentation.Relative)
			? Math.max(recording.workingTimeDuration, recording.duration)
			: this.maxDuration;
		let content = "";
		const projectTimeEnabled = globals.projectTimeEnabled;
		if (globals.workingTimeEnabled) {
			content += this.renderWorkingTimeBars(recording, durationAnchor);
			if (projectTimeEnabled) {
				content += `<div class="mb-5"></div>`;
			}
		}
		if (projectTimeEnabled) {
			content += this.renderProjectTimeBars(recording, durationAnchor);
		}
		return content;
	}

	private renderProjectTimeBars(recording: DashboardUserEntry, durationAnchor: number) {
		const progressBars = [{
			css: "progress-bar-projectTime-billable",
			progress: recording.durationBillable / durationAnchor
		}, {
			css: "progress-bar-projectTime-notBillable",
			progress: recording.durationNotBillable / durationAnchor
		}];

		let barScale = recording.duration / durationAnchor;
		if (recording.duration === 0 && durationAnchor === 0) {
			barScale = 0;
		}

		return renderProgress(progressBars, {
			barScale: barScale
		});
	}

	private renderWorkingTimeBars(recording: DashboardUserEntry, durationAnchor: number) {
		const workingTimeCategories: WorkingTimeCategory[] = recording.workingTimeCategories || [];

		const progressBars = workingTimeCategories.map(category => {
			const scale = category.duration / durationAnchor;

			return {
				css: "progress-bar-workingTimeCategory-" + category.category,
				progress: scale
			};
		});

		let barScale = recording.workingTimeDuration / durationAnchor;
		if (recording.workingTimeDuration === 0 && durationAnchor === 0) {
			barScale = 0;
		}

		return renderProgress(progressBars, {
			size: "progress-xxs",
			barScale: barScale
		});
	}
}

class WorkingTimeTypeTable extends DashboardTable<WorkingTimeTypeDashboardEntry, WorkingTimeTypeDashboardResponse> {
	private maxDuration = 0;

	constructor(public config: DashboardTableConfig) {
		super("/timr/reports/getWorkingTimeTypesForUserDashboard.json", config);

		this.onDataChanged((json: WorkingTimeTypeDashboardResponse) => {
			const timeTypeDurations = json.workingTimeTypes.results.map((timeType) => timeType.duration);
			this.maxDuration = Math.max(...timeTypeDurations);

			json.workingTimeTypes.results.forEach(recording => {
				recording.durationScale = recording.duration / this.maxDuration;
			});
		});
	}

	protected createDataTableConfig(): DataTables.Settings {
		return mergeProperties(super.createDataTableConfig(), {
			ajax: {
				dataSrc: "workingTimeTypes.results"
			}
		});
	}

	protected getColumnSettings(): TypedColumnSettings<WorkingTimeTypeDashboardEntry>[] {
		return [
			{data: "description", orderable: true, render: BasicDataTable.renderText},
			{
				data: "duration",
				width: UserDashboardPage.COLUMN_SIZE_DURATION,
				orderable: true,
				className: "text-right",
				render: BasicDataTable.renderDuration
			},
			{
				data: "durationScale",
				width: UserDashboardPage.COLUMN_SIZE_DURATION_BAR,
				orderable: false,
				render: this.renderDurationBar
			}
		];
	}

	protected createFooterDefinition(): FooterDefinition<WorkingTimeTypeDashboardResponse> {
		return {
			1: (json) => formatDuration(json.workingTimeTypes.total.duration)
		};
	}

	protected getDefaultOrder(): (string | number)[][] {
		return [[0, "asc"]];
	}

	private renderDurationBar(this: void, data: number, _type: string, recording: WorkingTimeTypeDashboardEntry, _meta: DataTables.CellMetaSettings) {
		return renderProgress({
			css: "progress-bar-workingTimeCategory-" + recording.category,
			progress: data
		}, {
			barScale: data
		});
	}
}

class RunningWorkingTimesTable extends DashboardTable<RunningWorkingTime, RunningWorkingTimesResponse> {
	constructor(config: BasicDataTableConfig) {
		super("/timr/reports/getRunningWorkingTimes.json", config);
		this.initRunningRecordingGpsPositionPopover();
	}

	protected createDataTableConfig(): DataTables.Settings {
		return mergeProperties(super.createDataTableConfig(), {
			ajax: {
				dataSrc: "workingTimes",
			}
		});
	}

	protected getColumnSettings(): TypedColumnSettings<RunningWorkingTime>[] {
		return [
			{
				data: "id",
				width: "44px",
				visible: globals.isAuthorizedForAllUsers,
				orderable: false,
				className: "context-menu",
				render: this.contextMenu.render
			},
			{
				data: "user.fullname",
				width: UserDashboardPage.COLUMN_SIZE_USER,
				orderable: true,
				render: BasicDataTable.renderText
			},
			{data: "workingTimeType.name", orderable: true, render: BasicDataTable.renderText},
			{
				data: "description",
				className: "pl-10 pr-10 table-data-description",
				orderable: true,
				render: BasicDataTable.renderDescriptionText
			},
			{
				data: "startPosition",
				width: UserDashboardPage.COLUMN_SIZE_GPS_ICON,
				orderable: false,
				render: BasicDataTable.renderPositionIcon,
				className: "gps-position  gps-position-start"
			},
			{
				data: "start",
				className: "pl-10 pr-10 time-column",
				orderable: true,
				render: BasicDataTable.renderDateTimeWithTimeZone
			},
			{
				data: "pausedAt",
				visible: globals.manualBreaksAllowed,
				width: UserDashboardPage.COLUMN_SIZE_PAUSED_AT,
				orderable: true,
				render: BasicDataTable.renderPausedAt
			},
			{
				data: "start",
				width: UserDashboardPage.COLUMN_SIZE_DURATION,
				className: "text-right",
				orderable: true,
				render: this.renderDurationSince
			}
		];
	}

	protected getDefaultOrder(): (string | number)[][] {
		return [[1, "desc"]];
	}

	protected createContextMenu() {
		return super.createContextMenu()
			.addMenuEntry({
				label: messages["report.userDashboard.context.delete.workingtime"],
				active: globals.isAuthorizedForAllUsers,
				action: (runningWorkingTime: RunningWorkingTime) => this.deleteWorkingTime(runningWorkingTime)
			});
	}

	private deleteWorkingTime(runningWorkingTime: RunningWorkingTime) {
		BootstrapModal.confirm({
			title: "dialog.delete.runningWorkingTime.title",
			message: messages.withArguments("dialog.delete.runningWorkingTime.message", runningWorkingTime.user.fullname),
			yesLabel: "confirmation.delete",
			yesAction: (modal) => {
				void api("reports/deleteWorkingTimeRecording.json")
					.post({
						userId: runningWorkingTime.user.id,
						recordId: runningWorkingTime.recordId
					})
					.done(() => {
						// Update status elements after deletion (TIMR-8406)
						statusElementsHolder.updateStatus();
						void this.reload();
					})
					.always(() => modal.close());
			},
			noLabel: "confirmation.cancel"
		});
	}
}

class RunningProjectTimesTable extends DashboardTable<RunningProjectTime, RunningProjectTimesResponse> {
	constructor(config: BasicDataTableConfig) {
		super("/timr/reports/getRunningProjectTimes.json", config);
		this.initRunningRecordingGpsPositionPopover();
	}

	protected createDataTableConfig(): DataTables.Settings {
		return mergeProperties(super.createDataTableConfig(), {
			ajax: {
				dataSrc: "projectTimes"
			}
		});
	}

	protected getColumnSettings(): TypedColumnSettings<RunningProjectTime>[] {
		return [
			{
				data: "id",
				width: "44px",
				visible: globals.isAuthorizedForAllUsers,
				orderable: false,
				className: "context-menu",
				render: this.contextMenu.render
			},
			{
				data: "user.fullname",
				width: UserDashboardPage.COLUMN_SIZE_USER,
				orderable: true,
				render: BasicDataTable.renderText
			},
			{data: "taskBreadCrumbs", orderable: true, render: BasicDataTable.renderRunningProjectTimeTaskPath},
			{
				data: "description",
				className: "table-data-description",
				orderable: true,
				render: BasicDataTable.renderDescriptionText
			},
			{
				data: "startPosition",
				width: UserDashboardPage.COLUMN_SIZE_GPS_ICON,
				orderable: false,
				render: BasicDataTable.renderPositionIcon,
				className: "gps-position  gps-position-start"
			},
			{
				data: "start",
				className: "pl-10 pr-10 time-column",
				orderable: true,
				render: BasicDataTable.renderDateTimeWithTimeZone
			},
			{
				data: "pausedAt",
				visible: globals.manualBreaksAllowed,
				width: UserDashboardPage.COLUMN_SIZE_PAUSED_AT,
				orderable: true,
				render: BasicDataTable.renderPausedAt
			},
			{
				data: "start",
				width: UserDashboardPage.COLUMN_SIZE_DURATION,
				className: "text-right",
				orderable: true,
				render: this.renderDurationSince
			}
		];
	}

	protected getDefaultOrder(): (string | number)[][] {
		return [[1, "desc"]];
	}

	protected createContextMenu() {
		return super.createContextMenu()
			.addMenuEntry({
				label: messages["report.userDashboard.context.delete.projecttime"],
				active: globals.isAuthorizedForAllUsers,
				action: (runningProjectTime: RunningProjectTime) => this.deleteProjectTime(runningProjectTime)
			});
	}

	private deleteProjectTime(runningProjectTime: RunningProjectTime) {
		BootstrapModal.confirm({
			title: "dialog.delete.runningProjectTime.title",
			message: messages.withArguments("dialog.delete.runningProjectTime.message", runningProjectTime.user.fullname),
			yesLabel: "confirmation.delete",
			yesAction: (modal) => {
				void api("reports/deleteProjectTimeRecording.json")
					.post({
						userId: runningProjectTime.user.id,
						recordId: runningProjectTime.recordId
					})
					.done(() => void this.reload())
					.always(() => modal.close());
			},
			noLabel: "confirmation.cancel"
		});
	}
}

class RunningDriveLogsTable extends DashboardTable<RunningDriveLog, RunningDriveLogsResponse> {
	constructor(config: BasicDataTableConfig) {
		super("/timr/reports/getRunningDriveLogs.json", config);
		this.initRunningRecordingGpsPositionPopover();
	}

	protected createDataTableConfig(): DataTables.Settings {
		return mergeProperties(super.createDataTableConfig(), {
			ajax: {
				dataSrc: "driveLogs"
			}
		});
	}

	protected getColumnSettings(): TypedColumnSettings<RunningDriveLog>[] {
		return [
			{
				data: "id",
				width: "44px",
				visible: globals.isAuthorizedForAllUsers,
				orderable: false,
				className: "context-menu",
				render: this.contextMenu.render
			},
			{
				data: "user.fullname",
				width: UserDashboardPage.COLUMN_SIZE_USER,
				orderable: true,
				render: BasicDataTable.renderText
			},
			{data: "car.plate", orderable: true, render: BasicDataTable.renderText},
			{data: "route", orderable: true, render: BasicDataTable.renderText},
			{data: "purpose", orderable: true, render: BasicDataTable.renderText},
			{data: "visited", orderable: false, render: BasicDataTable.renderText},
			{
				data: "startPosition",
				width: UserDashboardPage.COLUMN_SIZE_GPS_ICON,
				orderable: false,
				render: BasicDataTable.renderPositionIcon,
				className: "gps-position  gps-position-start"
			},
			{
				data: "start",
				className: "pl-10 pr-10 time-column",
				orderable: true,
				render: BasicDataTable.renderDateTimeWithTimeZone
			},
			{
				data: "start",
				width: UserDashboardPage.COLUMN_SIZE_DURATION,
				className: "text-right",
				orderable: true,
				render: this.renderDurationSince
			}
		];
	}

	protected getDefaultOrder(): (string | number)[][] {
		return [[1, "desc"]];
	}

	protected createContextMenu() {
		return super.createContextMenu()
			.addMenuEntry({
				label: messages["report.userDashboard.context.delete.drivelog"],
				active: globals.isAuthorizedForAllUsers,
				action: (runningDriveLog: RunningDriveLog) => this.deleteDriveLog(runningDriveLog)
			});
	}

	private deleteDriveLog(runningDriveLog: RunningDriveLog) {
		BootstrapModal.confirm({
			title: "dialog.delete.runningDriveLog.title",
			message: messages.withArguments("dialog.delete.runningDriveLog.message", runningDriveLog.user.fullname),
			yesLabel: "confirmation.delete",
			yesAction: (modal) => {
				void api("reports/deleteDriveLogRecording.json")
					.post({
						userId: runningDriveLog.user.id,
						recordId: runningDriveLog.recordId
					})
					.done(() => void this.reload())
					.always(() => modal.close());
			},
			noLabel: "confirmation.cancel"
		});
	}
}
